import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconLeft, IconRight } from "@assets";
import { Button, Form, Input, message, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { CheckOutlined } from "@ant-design/icons";
import { api } from "@api";
import { v4 as uuidv4 } from "uuid"; // Import uuid
import { useTranslation } from "react-i18next";

// Styled Components (unchanged)
const RootContainer = styled.div`
  position: relative;
  height: 100%;

  .ant-form {
    .buttons-wrapper {
      position: absolute;
      top: -70px;
      right: 12px;
    }

    .ant-btn {
      height: 48px;
      width: 172px;
    }
  }
`;

const Container = styled.div`
  border-radius: 8px;
  padding: 24px;
  background: ${(props) => props.theme.backgroundColor};
`;

const Topbar = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const TitleBar = styled.div`
  padding-bottom: 16px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${Title} {
    flex: 1;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const ItemCard = styled.div`
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  background-color: #fff;

  .ant-form {
    flex: 1;
    width: 100%;
    border-radius: 8px;
    background: #fff;
    padding: 0;

    .ant-input {
      width: 100%;
    }

    .ant-form-item {
      margin-bottom: 24px;

      :last-child {
        margin: 0;
      }
    }
  }

  .icon-delete {
    width: 48px;
    min-width: 48px;
    height: 48px;
    cursor: pointer;
  }
`;

const SpinWrapper = styled.div`
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

// Item Component
const Item = ({ item, form }) => {
  useEffect(() => {
    if (item) {
      // Store the original variant values with their IDs to compare later
      const initialVariantValues = item.VariantValues || [];
      form.setFieldsValue({
        name: item.name || "",
        values: initialVariantValues.map((v) => v.name).join(", "),
      });
      // Set a temporary field in the form or state to hold the original variant values with IDs
      // This is crucial for matching and generating UUIDs later.
      form.setFieldsValue({ originalVariantValues: initialVariantValues });
    }
  }, [item, form]);

  const {t} = useTranslation();

  return (
    <ItemCard>
      <Form layout="vertical" form={form}>
        <Form.Item
          name="name"
          label={t('option_name')}
          rules={[{ required: true, message: t('option_name_required') }]}
        >
          <Input placeholder={t('option_name')} />
        </Form.Item>
        <Form.Item
          name="values"
          label={t('value')}
          rules={[{ required: true, message: t('value_required') }]}
        >
          <Input placeholder={t('value_use_comma')} />
        </Form.Item>
      </Form>
    </ItemCard>
  );
};

// Main Component
const EditProductOptions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [updatedData, setUpdatedData] = useState({});
  const [form] = Form.useForm();

  const {t} = useTranslation();

  const getProductOptionDetails = async () => {
    try {
      const res = await api.getProductOptionDetail(id);
      const _data = res.data;

      setUpdatedData({
        id: _data.id,
        name: _data.name || "",
        VariantValues: Array.isArray(_data.VariantValues)
          ? _data.VariantValues
          : [],
      });
    } catch (error) {
      message.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductOptionDetails();
  }, [id]);

  const handleSave = async () => {
    try {
      setConfirmLoading(true);
      const values = await form.validateFields();
      const newVariantNames = values.values.split(",").map((v) => v.trim());
      const originalVariantValues =
        form.getFieldValue("originalVariantValues") || [];

      const payloadValues = newVariantNames.map((newName) => {
        const existingVariant = originalVariantValues.find(
          (original) => original.name === newName
        );

        if (existingVariant) {
          return { id: existingVariant.id, value: newName };
        } else {
          return { value: newName };
        }
      });

      const payload = {
        variant: {
          // <-- Make sure this 'variant' key is always present
          name: values.name,
          values: payloadValues,
        },
      };
      await api.editProductOption({ id, variant: payload.variant });
      message.success(t('product_option_success'));
      navigate(-1);
    } catch (error) {
      message.error(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <SpinWrapper>
        <Spin />
      </SpinWrapper>
    );
  }

  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          {t('back')}
        </Button>
        <TitleWrapper>
          <GrayTitle>{t('products')}</GrayTitle>
          <IconRight />
          <Title>{t('edit_product_options')}</Title>
        </TitleWrapper>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSave}
          loading={confirmLoading}
        >
          {t('save')}
        </Button>
      </Topbar>
      <Container>
        <TitleBar>
          <Title>{t('edit_product_options')}</Title>
        </TitleBar>
        <Item item={updatedData} form={form} />
      </Container>
    </RootContainer>
  );
};

export default EditProductOptions;
